import React, { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

/**
 * FreshContentChip — floating "new version available" notification.
 *
 * Why this exists:
 *   Previously the SW auto-reloaded the page the moment a new version
 *   activated. That destroyed in-progress work (typing a forum reply,
 *   half-completed booking flow, etc.) and felt jarring on a long-lived
 *   tab. This chip flips the contract: when a new build is detected
 *   in the foreground, we *invite* the user to refresh. Hidden tabs still
 *   auto-reload silently — only visible tabs get the chip.
 *
 * Wiring:
 *   `src/index.js` dispatches `sw:update-available` on the window when a
 *   new SW has reached the `installed` state and is waiting. The pending
 *   SW worker is stashed in `window._pendingNewSW` so click→activate can
 *   skipWaiting → controllerchange → reload (already in src/index.js).
 *
 *   If no pending SW is stashed (edge case), the click falls back to a
 *   plain `window.location.reload()`.
 */
export default function FreshContentChip() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onUpdate = () => setVisible(true);
    window.addEventListener("sw:update-available", onUpdate);
    return () => window.removeEventListener("sw:update-available", onUpdate);
  }, []);

  if (!visible || dismissed) return null;

  const refresh = () => {
    try {
      const pending = window._pendingNewSW;
      if (pending && typeof pending.postMessage === "function") {
        pending.postMessage({ type: "SKIP_WAITING" });
        // controllerchange handler in src/index.js will trigger the reload.
        return;
      }
    } catch (_) {}
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] bg-[#0e0e0e] text-white rounded-full shadow-[0_10px_30px_-8px_rgba(0,0,0,0.4)] pl-4 pr-1.5 py-1.5 flex items-center gap-3 mono text-[11px] sm:text-xs animate-[fadeInUp_240ms_ease-out]"
      data-testid="fresh-content-chip"
    >
      <span className="uppercase tracking-wider">
        <span className="text-[#fde047]">✨</span> New version available
      </span>
      <button
        onClick={refresh}
        className="inline-flex items-center gap-1.5 bg-[#ff4500] hover:bg-[#e63e00] rounded-full px-3 py-1.5 transition-colors uppercase tracking-wider font-bold"
        data-testid="fresh-content-refresh"
      >
        <RefreshCw className="w-3 h-3" strokeWidth={2.4} />
        Refresh
      </button>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="p-1 text-white/60 hover:text-white"
        data-testid="fresh-content-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
